/**
 * Task Filters Component
 *
 * Filter controls for the task board
 */

import { Filter, Plus, Search, X } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import type { TaskFilters, TaskPriority, TaskType } from '@/types/tasks';

interface TaskFiltersProps {
  readonly filters: TaskFilters;
  readonly onFiltersChange: (filters: Partial<TaskFilters>) => void;
  readonly onClearFilters: () => void;
  readonly onCreateTask?: () => void;
  /** When provided, overrides the default hardcoded task type options (used in live mode) */
  readonly taskTypeOptions?: { value: TaskType; label: string }[];
}

const taskTypes: { value: TaskType; label: string }[] = [
  { value: 'collection_case', label: 'Collections' },
  { value: 'promise', label: 'Promises' },
  { value: 'dispute', label: 'Disputes' },
  { value: 'transaction', label: 'Transaction' },
  { value: 'customer_action', label: 'Customer' },
  { value: 'task', label: 'Task' },
];

const priorities: { value: TaskPriority; label: string }[] = [
  { value: 'Critical', label: 'Critical' },
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' },
];

export function TaskFiltersComponent({
  filters,
  onFiltersChange,
  onClearFilters,
  onCreateTask,
  taskTypeOptions,
}: TaskFiltersProps) {
  const [searchQuery, setSearchQuery] = useState(filters.searchQuery || '');

  const handleTypeToggle = (type: TaskType) => {
    const currentTypes = filters.type || [];
    const newTypes = currentTypes.includes(type) ? currentTypes.filter((t) => t !== type) : [...currentTypes, type];
    onFiltersChange({ type: newTypes.length > 0 ? newTypes : undefined });
  };

  const handlePriorityToggle = (priority: TaskPriority) => {
    const currentPriorities = filters.priority || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter((p) => p !== priority)
      : [...currentPriorities, priority];
    onFiltersChange({ priority: newPriorities.length > 0 ? newPriorities : undefined });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    onFiltersChange({ searchQuery: value || undefined });
  };

  const activeFilterCount =
    (filters.type?.length || 0) + (filters.priority?.length || 0) + (filters.searchQuery ? 1 : 0);

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Search */}
      <div className="relative flex-1 min-w-[200px] max-w-[400px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Create New Task Button */}
      {onCreateTask && (
        <Button onClick={onCreateTask} className="bg-teal-600 hover:bg-teal-700 ml-auto">
          <Plus className="h-4 w-4 mr-2" />
          Create New Task
        </Button>
      )}

      {/* Type Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Type
            {filters.type && filters.type.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                {filters.type.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Task Type</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {(taskTypeOptions ?? taskTypes).map((type) => (
            <DropdownMenuCheckboxItem
              key={type.value}
              checked={filters.type?.includes(type.value) || false}
              onCheckedChange={() => handleTypeToggle(type.value)}
            >
              {type.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Priority Filter */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Priority
            {filters.priority && filters.priority.length > 0 && (
              <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center">
                {filters.priority.length}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuLabel>Priority Level</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {priorities.map((priority) => (
            <DropdownMenuCheckboxItem
              key={priority.value}
              checked={filters.priority?.includes(priority.value) || false}
              onCheckedChange={() => handlePriorityToggle(priority.value)}
            >
              {priority.label}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Clear Filters */}
      {activeFilterCount > 0 && (
        <Button variant="ghost" size="sm" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-2" />
          Clear ({activeFilterCount})
        </Button>
      )}
    </div>
  );
}
